import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { AuthUser } from './auth.types'
import axios from 'axios'
import { apiClient, apiOrigin } from '../../lib/apiClient'
import type { ApiResponse } from '../../types'
import { useQueryClient } from '@tanstack/react-query'

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isInitializing: boolean
  establishSession: (user: AuthUser) => void
  clearSession: () => void
  login: (login: string, password: string) => Promise<AuthUser>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)

  const establishSession = useCallback((nextUser: AuthUser) => setUser(nextUser), [])
  const clearSession = useCallback(() => setUser(null), [])

  useEffect(() => {
    window.addEventListener('auth:unauthorized', clearSession)
    return () => window.removeEventListener('auth:unauthorized', clearSession)
  }, [clearSession])

  useEffect(() => {
    let active = true
    apiClient.get<ApiResponse<AuthUser>>('/auth/user')
      .then(({ data }) => { if (active) setUser(data.data) })
      .catch(() => { if (active) setUser(null) })
      .finally(() => { if (active) setIsInitializing(false) })
    return () => { active = false }
  }, [])

  useEffect(() => {
    const refresh = () => {
      if (user) void apiClient.get<ApiResponse<AuthUser>>('/auth/user').then(({ data }) => setUser(current => current?.id === data.data.id ? data.data : current)).catch(() => {})
    }
    window.addEventListener('auth:profile-updated', refresh)
    return () => window.removeEventListener('auth:profile-updated', refresh)
  }, [user])

  const login = useCallback(async (loginValue: string, password: string) => {
    await axios.get(`${apiOrigin}/sanctum/csrf-cookie`, { withCredentials: true, withXSRFToken: true })
    const { data } = await apiClient.post<ApiResponse<AuthUser>>('/auth/login', { login: loginValue, password })
    setUser(data.data)
    return data.data
  }, [])

  const logout = useCallback(async () => {
    await apiClient.post('/auth/logout')
    setUser(null)
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isAuthenticated: user !== null,
    isInitializing,
    establishSession,
    clearSession,
    login,
    logout,
  }), [clearSession, establishSession, isInitializing, login, logout, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
